import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Stack, Typography } from "@mui/material";
import { ExecutionResultPanel, PALETTE, SectionHeader, SectionPaper, StatusChip } from "../components/shared";
import type { PlatformSubmission } from "../utils/platformSubmissions";

type Props = {
  submission: PlatformSubmission;
  onBack: () => void;
  backLabel?: string;
};

export default function ProblemSubmissionDetailView({
  submission,
  onBack,
  backLabel = "Back to Submissions",
}: Props) {
  const raw = submission.raw ?? {};
  const code = raw.code ?? "";

  const submittedAt = submission.submittedAt
    ? new Date(submission.submittedAt).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: "none", mb: 1.5, fontSize: 12 }}>
        {backLabel}
      </Button>

      <SectionPaper sx={{ mb: 1.25 }}>
        <Box sx={{ p: 1.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16, color: "primary.main", mb: 0.75 }}>
            {submission.title}
          </Typography>
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
            <StatusChip status={submission.status} />
            {submission.language && (
              <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary, textTransform: "capitalize" }}>
                {submission.language}
              </Typography>
            )}
            <Typography sx={{ fontSize: 12, color: PALETTE.textMuted }}>{submittedAt}</Typography>
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary }}>
              Tests: {submission.passedTestCases ?? 0}/{submission.totalTestCases ?? 0}
            </Typography>
            <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary }}>
              Runtime: {submission.runtimeMs ? `${(submission.runtimeMs / 1000).toFixed(2)}s` : "—"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary }}>
              Memory: {submission.memoryKb ? `${submission.memoryKb} KB` : "—"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary }}>
              XP: +{submission.xpEarned ?? 0}
            </Typography>
          </Stack>
        </Box>
      </SectionPaper>

      {code ? (
        <SectionPaper sx={{ mb: 1.25 }}>
          <SectionHeader title="Submitted Solution" />
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              fontSize: 12,
              lineHeight: 1.55,
              fontFamily: "monospace",
              bgcolor: "#1e293b",
              color: "#e2e8f0",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {code}
          </Box>
        </SectionPaper>
      ) : (
        <SectionPaper sx={{ mb: 1.25, p: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: PALETTE.textSecondary }}>
            No submitted code available for this submission.
          </Typography>
        </SectionPaper>
      )}

      {(raw.testCaseResults?.length || raw.status) && (
        <ExecutionResultPanel execution={raw} />
      )}
    </Box>
  );
}
